import { useEffect, useState, useCallback } from "react";
import { FileSpreadsheet, Plus, Trash2, Pencil, Download, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import TemplateAnalysisDialog from "@/components/TemplateAnalysisDialog";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function SystemTemplates() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(null); // {id, name}
  const [uploadName, setUploadName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Analysis dialog state — analysis + which template id it belongs to (for autofix).
  const [analysis, setAnalysis] = useState(null); // {templateId, templateName, data}

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/system-quote-templates");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const submitUpload = async () => {
    if (!uploadName.trim() || !uploadFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const { data } = await api.post(
        `/system-quote-templates?name=${encodeURIComponent(uploadName.trim())}`,
        fd,
      );
      toast.success(t("systemTemplates.uploaded"));
      setUploadOpen(false);
      setUploadName(""); setUploadFile(null);
      fetchTemplates();
      // Show analysis right after upload (mirrors the customer upload flow).
      if (data?.analysis) {
        setAnalysis({ templateId: data.template.id, templateName: data.template.name, data: data.analysis });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("systemTemplates.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const submitRename = async () => {
    if (!renameOpen?.name?.trim()) return;
    try {
      await api.patch(`/system-quote-templates/${renameOpen.id}`, { name: renameOpen.name.trim() });
      toast.success(t("systemTemplates.renamed"));
      setRenameOpen(null);
      fetchTemplates();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(t("systemTemplates.deleteConfirm").replace("{name}", tpl.name))) return;
    try {
      await api.delete(`/system-quote-templates/${tpl.id}`);
      toast.success(t("systemTemplates.deleted"));
      fetchTemplates();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleAnalyze = async (tpl) => {
    try {
      const { data } = await api.get(`/system-quote-templates/${tpl.id}/analyze`);
      setAnalysis({ templateId: tpl.id, templateName: tpl.name, data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleAutofix = async () => {
    if (!analysis?.templateId) return;
    try {
      const { data } = await api.post(`/system-quote-templates/${analysis.templateId}/autofix`);
      toast.success(t("templateAnalysis.autofixSuccess").replace("{count}", data.fixed_count));
      setAnalysis({ ...analysis, data: data.analysis });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("templateAnalysis.autofixFailed"));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="p-6 space-y-4" data-testid="system-templates-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet size={22} className="text-brand" />
            {t("systemTemplates.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("systemTemplates.subtitle")}</p>
        </div>
        <Button
          onClick={() => setUploadOpen(true)}
          className="rounded-md bg-brand hover:bg-brand-hover gap-1.5"
          data-testid="system-templates-upload-button"
        >
          <Plus size={15} /> {t("systemTemplates.uploadButton")}
        </Button>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table data-testid="system-templates-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[240px]">{t("systemTemplates.nameLabel")}</TableHead>
              <TableHead>{t("systemTemplates.uploadedBy")}</TableHead>
              <TableHead>{t("systemTemplates.uploadedAt")}</TableHead>
              <TableHead className="text-right w-[380px]">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-10 text-muted-foreground" data-testid="system-templates-empty">
                  {t("systemTemplates.empty")}
                </TableCell>
              </TableRow>
            )}
            {items.map((tpl) => (
              <TableRow key={tpl.id} data-testid={`system-template-row-${tpl.id}`}>
                <TableCell className="font-medium">{tpl.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{tpl.uploaded_by || "-"}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{fmtDate(tpl.uploaded_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button
                      size="sm" variant="outline" className="rounded-md h-8 gap-1"
                      onClick={() => handleAnalyze(tpl)}
                      data-testid={`system-template-analyze-${tpl.id}`}
                    >
                      <ShieldCheck size={13} /> {t("systemTemplates.analyze")}
                    </Button>
                    <a
                      href={`${BACKEND}${tpl.url}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 h-8 px-3 text-xs rounded-md border border-border hover:bg-secondary transition-colors"
                      data-testid={`system-template-download-${tpl.id}`}
                    >
                      <Download size={13} /> {t("systemTemplates.download")}
                    </a>
                    <Button
                      size="sm" variant="outline" className="rounded-md h-8 gap-1"
                      onClick={() => setRenameOpen({ id: tpl.id, name: tpl.name })}
                      data-testid={`system-template-rename-${tpl.id}`}
                    >
                      <Pencil size={13} /> {t("systemTemplates.rename")}
                    </Button>
                    <Button
                      size="sm" variant="outline" className="rounded-md h-8 gap-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      onClick={() => handleDelete(tpl)}
                      data-testid={`system-template-delete-${tpl.id}`}
                    >
                      <Trash2 size={13} /> {t("systemTemplates.delete")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" data-testid="system-templates-upload-dialog">
          <DialogHeader>
            <DialogTitle>{t("systemTemplates.uploadTitle")}</DialogTitle>
            <DialogDescription>{t("systemTemplates.uploadHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tpl-name">{t("systemTemplates.nameLabel")}</Label>
              <Input
                id="tpl-name"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder={t("systemTemplates.namePlaceholder")}
                data-testid="system-template-name-input"
              />
            </div>
            <div>
              <Label htmlFor="tpl-file">{t("systemTemplates.fileLabel")}</Label>
              <Input
                id="tpl-file"
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="system-template-file-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="rounded-md">{t("common.cancel")}</Button>
            <Button
              onClick={submitUpload}
              disabled={uploading || !uploadName.trim() || !uploadFile}
              className="rounded-md bg-brand hover:bg-brand-hover"
              data-testid="system-template-upload-submit"
            >
              {uploading ? "…" : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameOpen} onOpenChange={(v) => { if (!v) setRenameOpen(null); }}>
        <DialogContent className="max-w-md" data-testid="system-templates-rename-dialog">
          <DialogHeader>
            <DialogTitle>{t("systemTemplates.renameTitle")}</DialogTitle>
          </DialogHeader>
          <Input
            value={renameOpen?.name || ""}
            onChange={(e) => setRenameOpen({ ...renameOpen, name: e.target.value })}
            data-testid="system-template-rename-input"
            onKeyDown={(e) => e.key === "Enter" && submitRename()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(null)} className="rounded-md">{t("common.cancel")}</Button>
            <Button onClick={submitRename} className="rounded-md bg-brand hover:bg-brand-hover" data-testid="system-template-rename-submit">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemplateAnalysisDialog
        open={!!analysis}
        onClose={() => setAnalysis(null)}
        templateName={analysis?.templateName}
        analysis={analysis?.data}
        onAutofix={handleAutofix}
      />
    </div>
  );
}
